<template>
    <div>
        <div class="shadow sm:rounded-md sm:overflow-hidden">
            <div class="bg-white py-6 px-4 space-y-6 sm:p-6">
                <div class="grid grid-cols-11 gap-6">
                    <div class="col-span-11 lg:col-span-4" v-if="licenseTypeOptions.length">
                        <apexo-input-group :label="$t('orders.activations.activationForm.licenseType')">
                            <apexo-select
                                button-class-name="w-full text-left"
                                :options="licenseTypeOptions"
                                v-model="type"
                                v-if="licenseTypeOptions.length > 1"
                            />

                            <apexo-input
                                classes="w-full"
                                :disabled="true"
                                :value="licenseTypeOptions[0].label"
                                v-else
                            />
                        </apexo-input-group>
                    </div>

                    <template v-if="isActivatingOEM">
                        <div class="col-span-11 lg:col-span-4">
                            <apexo-input-group :label="$t('orders.activations.activationForm.deviceSerial')">
                                <apexo-input classes="flex-1" ref="deviceSerial" v-model="deviceSerial" />
                            </apexo-input-group>
                        </div>

                        <div class="col-span-11 lg:col-span-3">
                            <apexo-input-group :label="$t('orders.activations.activationForm.firmwareVersion')">
                                <apexo-input classes="flex-1" v-model="firmwareVersion" />
                            </apexo-input-group>
                        </div>

                        <div class="hidden lg:block lg:col-span-4" />
                    </template>

                    <template v-else-if="isActivatingAndroid">
                        <div class="col-span-11 lg:col-span-2">
                            <apexo-input-group :label="$t('orders.activations.activationForm.phoneModel')">
                                <apexo-input classes="flex-1" v-model="phoneModel" />
                            </apexo-input-group>
                        </div>

                        <div class="col-span-11 lg:col-span-2">
                            <apexo-input-group :label="$t('orders.activations.activationForm.androidVersion')">
                                <apexo-input classes="flex-1" v-model="androidVersion" />
                            </apexo-input-group>
                        </div>

                        <div class="col-span-11 lg:col-span-3">
                            <apexo-input-group :label="$t('orders.activations.activationForm.phoneSerial')">
                                <apexo-input classes="flex-1" v-model="phoneSerial" />
                            </apexo-input-group>
                        </div>

                        <div class="hidden lg:block lg:col-span-4" />
                    </template>

                    <template v-else-if="isActivatingPC">
                        <div class="col-span-11 lg:col-span-3">
                            <apexo-input-group :label="$t('orders.activations.activationForm.computerSerial')">
                                <apexo-input classes="flex-1" v-model="computerSerial" />
                            </apexo-input-group>
                        </div>
                    </template>

                    <div class="col-span-11 lg:col-span-4">
                        <apexo-input-group :label="$t('orders.activations.activationForm.afsSerial')">
                            <apexo-input classes="flex-1" v-model="afsSerial" />
                        </apexo-input-group>
                    </div>

                    <div class="col-span-11 lg:col-span-3">
                        <apexo-input-group :hint="latestAFSVersion" :label="$t('orders.activations.activationForm.afsVersion')">
                            <apexo-input
                                classes="flex-1"
                                @enter-pressed="activateLicense"
                                v-model="afsVersion"
                            />
                        </apexo-input-group>
                    </div>
                </div>
            </div>

            <div class="flex flex-row px-4 py-3 bg-gray-50 items-center justify-between sm:px-6">
                <p class="text-gray-700 text-sm">
                    {{ $t('orders.activations.activationForm.availableLicenses', { count: remainingLicenses }) }}
                </p>

                <div class="flex flex-row items-center">
                    <p class="mr-4 text-sm text-red-800" v-if="!!error">
                        {{ $t('orders.activations.activationForm.errors.activationFailed') }}
                    </p>

                    <span class="inline-flex rounded-md shadow-sm">
                        <apexo-button :disabled="isActivationDisabled" @click="activateLicense">
                            {{ $t('orders.activations.activationForm.activate') }}
                        </apexo-button>
                    </span>
                </div>
            </div>
        </div>

        <license-modal name="form-license-key" @closed="focusDeviceSerialField()" />
    </div>
</template>

<script>
    import { mapMutations } from 'vuex';

    import ApexoButton from '@/components/Elements/ApexoButton.vue';
    import ApexoInput from '@/components/Elements/ApexoInput.vue';
    import ApexoInputGroup from '@/components/Elements/ApexoInputGroup.vue';
    import LicenseModal from '@/components/Activation/LicenseModal.vue';
    import Platform from '@/models/Platform.js';

    export default {
        components: {
            ApexoInputGroup,
            ApexoButton,
            ApexoInput,
            LicenseModal,
        },

        props: {
            licenseTypeOptions: {
                type: Array,
                default: () => [],
            },
        },

        data() {
            return {
                type: null,
                afsSerial: '',
                afsVersion: '',
                deviceSerial: '',
                error: '',
                firmwareVersion: '',
                phoneSerial: '',
                androidVersion: '',
                phoneModel: '',
                computerSerial: '',
                currentAfsVersion: '1.0.0',
            };
        },

        computed: {
            selectedLicenseType() {
                return this.licenseTypeOptions.find(type => type.value === this.type);
            },

            remainingLicenses() {
                return this.selectedLicenseType
                    ? this.selectedLicenseType.count
                    : 0;
            },

            selectedLicenseTypePlatform() {
                return this.selectedLicenseType
                    ? this.selectedLicenseType.platform
                    : '';
            },

            isActivatingOEM() {
                return this.selectedLicenseTypePlatform === Platform.OEM;
            },

            isActivatingAndroid() {
                return this.selectedLicenseTypePlatform === Platform.ANDROID;
            },

            isActivatingPC() {
                return this.selectedLicenseTypePlatform === Platform.PC;
            },

            isActivationDisabled() {
                if (!this.afsSerial || !this.afsVersion) {
                    return true;
                }

                if (this.isActivatingOEM) {
                    return !this.deviceSerial ||
                        !this.firmwareVersion;
                }

                if (this.isActivatingAndroid) {
                    return !this.phoneSerial ||
                        !this.phoneModel ||
                        !this.androidVersion;
                }

                if (this.isActivatingPC) {
                    return !this.computerSerial;
                }

                return false;
            },

            latestAFSVersion() {
                return `${this.$t('orders.activations.activationForm.latestVersion')}${this.currentAfsVersion}`;
            },
        },

        methods: {
            ...mapMutations([
                'decreaseLicenseTypeUnactivatedCount',
            ]),

            async activateLicense() {
                if (this.isActivationDisabled) {
                    return;
                }

                this.error = '';

                const payload = {
                    afs_serial_number: this.afsSerial,
                    android_version: this.androidVersion,
                    afs_version: this.afsVersion,
                    device_serial_number: this.deviceSerial || this.phoneSerial || this.computerSerial,
                    firmware_version: this.firmwareVersion,
                    license_type_id: this.type,
                    phone_model: this.phoneModel,
                };

                try {
                    const response = await this.$axios.put('/licenses/activate', payload);
                    const updatedLicense = response.data.data;
                    console.log(this.selectedLicenseType.value);
                    this.decreaseLicenseTypeUnactivatedCount(this.selectedLicenseType.value);

                    this.openLicenseKeyModal(updatedLicense.key);

                    this.reset();

                    this.$emit('onLicenseActivated', updatedLicense);
                } catch (error) {
                    this.error = error;
                    console.log(error);
                }
            },

            reset() {
                this.afsSerial = '';
                this.deviceSerial = '';
                this.firmwareVersion = '';
                this.phoneSerial = '';
                this.androidVersion = '';
                this.afsVersion = '';
                this.phoneModel = '';
                this.computerSerial = '';
            },

            openLicenseKeyModal(key) {
                this.$modal.open('form-license-key', { licenseKey: key });
            },

            focusDeviceSerialField() {
                this.$refs.deviceSerial.focus();
            },
        },

        mounted() {
            if (!this.licenseTypeOptions.length) {
                return;
            }

            this.type = this.licenseTypeOptions[0].value;
        },
    };
</script>
